import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const Lease = () => {
  // State variables for the lease calculation
  const [capitalizedCost, setCapitalizedCost] = useState(0);
  const [residualValue, setResidualValue] = useState(0);
  const [leaseTerm, setLeaseTerm] = useState(0);
  const [moneyFactor, setMoneyFactor] = useState(0);
  const [monthlyLeasePayment, setMonthlyLeasePayment] = useState(0);

  // Event handlers to update state values and ensure the inputs are valid numbers
  const handleCapitalizedCostChange = (e) =>
    setCapitalizedCost(parseFloat(e.target.value) || 0);
  const handleResidualValueChange = (e) =>
    setResidualValue(parseFloat(e.target.value) || 0);
  const handleLeaseTermChange = (e) =>
    setLeaseTerm(parseFloat(e.target.value) || 0);
  const handleMoneyFactorChange = (e) =>
    setMoneyFactor(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate the monthly lease payment when inputs change
  useEffect(() => {
    if (capitalizedCost && residualValue && leaseTerm && moneyFactor) {
      const payment =
        (capitalizedCost - residualValue) / leaseTerm +
        capitalizedCost * moneyFactor;
      setMonthlyLeasePayment(payment);
    } else {
      setMonthlyLeasePayment(0);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });

  }, [capitalizedCost, residualValue, leaseTerm, moneyFactor]);

  return (
    <Layout title="Lease Calculator" path="lease">
      <section className="section-padding" id="lease-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Lease Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Lease Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Capitalized Cost Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="capitalizedCost" className="form-label">
                        Capitalized Cost (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Capitalized Cost:</strong> The total cost of the leased item, including its price, taxes, and fees."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="capitalizedCost"
                        placeholder="Enter capitalized cost"
                        value={capitalizedCost || ""}
                        onChange={handleCapitalizedCostChange}
                      />
                    </div>

                    {/* Residual Value Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="residualValue" className="form-label">
                        Residual Value (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Residual Value:</strong> The estimated value of the item at the end of the lease term."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="residualValue"
                        placeholder="Enter residual value"
                        value={residualValue || ""}
                        onChange={handleResidualValueChange}
                      />
                    </div>

                    {/* Lease Term Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="leaseTerm" className="form-label">
                        Lease Term (months)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Lease Term:</strong> The duration of the lease agreement in months."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="leaseTerm"
                        placeholder="Enter lease term in months"
                        value={leaseTerm || ""}
                        onChange={handleLeaseTermChange}
                      />
                    </div>

                    {/* Money Factor Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="moneyFactor" className="form-label">
                        Money Factor
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Money Factor:</strong> A small decimal number representing the interest rate of the lease."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        step="0.0001"
                        className="form-control"
                        id="moneyFactor"
                        placeholder="Enter money factor"
                        value={moneyFactor || ""}
                        onChange={handleMoneyFactorChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Monthly Lease Payment:{" "}
                        <span className="fw-bold ms-3">
                          {monthlyLeasePayment === 0
                            ? "0.00"
                            : monthlyLeasePayment.toFixed(2)}{" "}
                          ₹
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Lease Calculator
                </h3>

                {/* What is a Lease Calculator? */}
                <h4 className="mt-3 mb-3">What is a Lease Calculator?</h4>
                <p>
                  A **Lease Calculator** helps businesses and individuals
                  calculate the monthly payments for leasing vehicles or
                  equipment. It takes into account the capitalized cost (initial
                  cost), the residual value (value at the end of the lease), the
                  lease term, and the money factor (interest rate).
                </p>

                {/* Why Use a Lease Calculator? */}
                <h4 className="mt-4 mb-3">Why Use a Lease Calculator?</h4>
                <p>
                  This calculator helps businesses and individuals evaluate the
                  costs associated with leasing versus buying. It provides
                  transparency in monthly lease payments, making it easier to
                  plan financially.
                </p>

                {/* Benefits of Using a Lease Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using a Lease Calculator
                </h4>
                <ul>
                  <li>
                    <strong>Helps Evaluate Leasing vs. Buying:</strong> Provides
                    insights into the monthly cost of leasing as compared to
                    purchasing the vehicle or equipment.
                  </li>
                  <li>
                    <strong>Provides Transparency in Lease Costs:</strong>{" "}
                    Clearly breaks down the monthly lease payments, including
                    interest costs.
                  </li>
                  <li>
                    <strong>Supports Financial Planning and Budgeting:</strong>{" "}
                    Helps businesses and individuals plan their finances by
                    estimating future monthly payments and total lease costs.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">
                  Who Should Use a Lease Calculator?
                </h4>
                <p>
                  The lease calculator is ideal for anyone considering leasing a
                  vehicle or equipment, including:
                </p>
                <ul>
                  <li>
                    <strong>Automotive Industry:</strong> Car dealerships, auto
                    manufacturers, and customers looking to lease vehicles.
                  </li>
                  <li>
                    <strong>Equipment Rentals:</strong> Businesses that lease
                    machinery, construction equipment, or office equipment.
                  </li>
                  <li>
                    <strong>Real Estate:</strong> Real estate agents or
                    businesses offering property leasing services.
                  </li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">
                  How do I calculate the monthly lease payment?
                </h4>
                <p>
                  To calculate the monthly lease payment, subtract the residual
                  value from the capitalized cost, divide that by the lease term
                  (in months), and then add the money factor multiplied by the
                  capitalized cost.
                </p>

                <h4 className="mt-4 mb-3">
                  Why is the money factor important?
                </h4>
                <p>
                  The money factor represents the interest rate on the lease. A
                  higher money factor means higher monthly payments, so it’s
                  crucial to factor this into your calculation when considering
                  leasing options.
                </p>

                <h4 className="mt-4 mb-3">
                  What’s the difference between leasing and buying?
                </h4>
                <p>
                  Leasing allows you to pay for the use of an asset for a
                  specified period, while buying means you own the asset
                  outright. Leasing often results in lower monthly payments but
                  doesn’t provide ownership at the end of the term.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Lease;
